import { Plus } from "lucide-react";
import { useEffect, useState } from "react";
import t from "@/lang/lang";
import { Process } from "@/services/backend/processes/types/process";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import {
  CommonDialogFooter,
  CommonDialogHeader,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";
import {
  CreateCommitmentsFromDocumentRequest,
  useRelationshipsCommitmentsFromDocumentMutation,
} from "@/services/backend/relationships/api/commitment-from-document-api";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Label } from "@/shared/components/ui/label";
import { RelationshipSelect } from "@/services/backend/relationships/components/relationship-select";
import { InputValidationErrors } from "@/shared/components/errors/input-error-messages";
import { ConfirmationSelect } from "@/services/backend/vbs/confirmations/components/confirmation-select";
import { useVbsConfirmationShowQuery } from "@/services/backend/vbs/confirmations/api/confirmation-api";
import { NullUUID } from "@bitory-gmbh/nidavellir";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { DateInput } from "@/shared/components/form/date-input";

export function CreateCommitmentsFromConfirmationDialog({
  process,
}: {
  process: Process;
}) {
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState<CreateCommitmentsFromDocumentRequest>({
    relationshipId: "",
    documentId: "",
    documentKind: GSMDocumentKind.Confirmation,
    serviceKind: process.serviceKind,
    processId: process.id,
    splitByCycle: true,
    date: new Date().toISOString(),
  });
  const [patch, { isLoading, error, isSuccess, reset }] =
    useRelationshipsCommitmentsFromDocumentMutation();
  const { data: confirmation } = useVbsConfirmationShowQuery({
    id: request.documentId,
  });

  useEffect(() => {
    if (isSuccess) {
      setOpen(false);
      setRequest({
        relationshipId: "",
        documentId: "",
        documentKind: GSMDocumentKind.Confirmation,
        serviceKind: process.serviceKind,
        processId: process.id,
        splitByCycle: true,
        date: new Date().toISOString(),
      });
      reset();
    }
  }, [isSuccess, process.id, process.serviceKind, reset]);

  const doCreate = () => {
    if (isLoading) return;

    patch(request);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <CommonDialogTrigger
        icon={Plus}
        label={t("Sollstellung")}
        size="sm"
        disabled={!process.customerId}
      />
      <DialogContent className="sm:max-w-fit">
        <CommonDialogHeader
          title={t("Sollstellung(en) erstellen")}
          description={t(
            "Es können Sollstellungen aus einer Auftragsbestätigung erstellt werden. Dabei werden die Daten aus der Auftragsbestätigung übernommen.",
          )}
        />
        <div className="grid content-start gap-1.5">
          <Label>{t("Auftragsbestätigung")}</Label>
          <ConfirmationSelect
            value={request.documentId}
            onValueChange={(value) =>
              setRequest((r) => ({ ...r, documentId: value }))
            }
            listRequest={{
              process: { active: true, values: [process.id] },
              immutable: { active: true, values: [true] },
            }}
          />
          <InputValidationErrors error={error} field="documentId" />
        </div>
        <div className="grid content-start gap-1.5">
          <Label>{t("Beziehung")}</Label>
          <RelationshipSelect
            value={request.relationshipId}
            onValueChange={(value) =>
              setRequest((r) => ({ ...r, relationshipId: value }))
            }
            listRequest={{
              company: { active: true, values: [process.companyId] },
              branch: { active: true, values: process.branches },
              customer: { active: true, values: [process.customerId!] },
              serviceObject: {
                active: true,
                values: [
                  confirmation ? confirmation.serviceObjectId : NullUUID,
                ],
              },
            }}
          />
          <span className="text-muted-foreground text-sm">
            {t(
              "Eine Sollstellung ist immer einer Beziehung zugeordnet. Beziehungen bestehen zwischen einer Niederlassung und einem Objekt. Sollten keine Beziehungen angezeigt werden, muss eine passende für diesen Vorgang erstellt werden.",
            )}
          </span>
          <InputValidationErrors error={error} field="relationshipId" />
        </div>
        <DateInput
          label={t("Erste Sollstellung")}
          date={request.date}
          onDateChange={(date) => setRequest({ ...request, date: date ?? "" })}
          showWeekNumber
        />
        <div className="flex items-center gap-3">
          <Checkbox
            id="splitByCycle"
            checked={request.splitByCycle}
            onCheckedChange={(checked) =>
              setRequest({ ...request, splitByCycle: Boolean(checked) })
            }
          />
          <Label htmlFor="splitByCycle">{t("Nach Turnus aufteilen")}</Label>
        </div>
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter
          isLoading={isLoading}
          onClickAction={doCreate}
          actionLabel={t("Erstellen")}
        />
      </DialogContent>
    </Dialog>
  );
}
